import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'svz-validation-message',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-validation-message.component.html',
  providers: [DatePipe],
  host: {
    class: 'block',
  },
  standalone: true,
})
export class SvzValidationMessageComponent {
  private readonly datePipe = inject(DatePipe);
  private readonly translocoService = inject(TranslocoService);

  readonly control = input.required<AbstractControl | null>();

  readonly showWhenDirty = input<boolean>(true);

  get validationMessage(): { message: string } | null {
    const control = this.control();
    if (!control) {
      return null;
    }

    const canShow = this.showWhenDirty() ? control.dirty || control.touched : control.touched;
    if (!canShow || !control.errors) {
      return null;
    }

    for (const validator of Object.keys(control.errors)) {
      return this.formatValidationMessage(validator, control.errors[validator]);
    }

    return null;
  }

  private translate(text: string): string {
    return this.translocoService.translate(text);
  }

  private formatValidationMessage(validator: string, value: unknown): { message: string } {
    const messages: Record<string, (payload: any) => { message: string }> = {
      error: () => ({ message: this.translate('O campo está com erro') }),
      required: () => ({ message: this.translate('Campo obrigatório') }),
      mustMatch: () => ({ message: this.translate('As senhas devem ser iguais') }),
      invalidDate: () => ({ message: this.translate('Data inválida') }),
      minDate: (payload: { date: Date; format: string }) => ({
        message: `${this.translate('A data deve ser maior ou igual a')} ${this.datePipe.transform(payload.date, payload.format)}`,
      }),
      maxDate: (payload: { date: Date; format: string }) => ({
        message: `${this.translate('A data deve ser menor ou igual a')} ${this.datePipe.transform(payload.date, payload.format)}`,
      }),
      maxlength: (payload: { requiredLength: number }) => ({
        message: `${this.translate('O conteúdo deve conter o número máximo de caracteres')}: ${payload.requiredLength}`,
      }),
      minlength: (payload: { requiredLength: number }) => ({
        message: `${this.translate('O conteúdo deve conter o número mínimo de caracteres')}: ${payload.requiredLength}`,
      }),
      max: (payload: { max: number }) => ({ message: `${this.translate('O valor máximo é')}: ${payload.max}` }),
      min: (payload: { min: number }) => ({ message: `${this.translate('O valor mínimo é')}: ${payload.min}` }),
      spaceNotAllowed: () => ({ message: this.translate('O campo não pode conter espaços') }),
      invalid24Hour: () => ({ message: this.translate('Insira um horário válido no formato HH:MM') }),
      invalidDecimalPlaces: () => ({ message: this.translate('O campo deve conter no máximo 6 casas decimais') }),
      selectPeriodMaxRange: () => ({ message: this.translate('Limite de pesquisa 1 ano') }),
    };

    return messages[validator] ? messages[validator](value) : messages.error(value);
  }
}
